import React, { useEffect, useMemo, useState } from 'react';
import { userService } from '../services/userService';

const approvalOptions = ['pending', 'approved', 'rejected'];
const roleOptions = ['user', 'admin'];
const subscriptionPlans = ['free', 'pro', 'business', 'enterprise'];
const subscriptionStatuses = ['trialing', 'active', 'past_due', 'canceled'];
const availableFeatures = [
  { key: 'custom_domains', label: 'Custom domains' },
  { key: 'advanced_analytics', label: 'Advanced analytics' },
  { key: 'forms', label: 'Forms' },
  { key: 'storefront', label: 'Storefront' },
  { key: 'r2_uploads', label: 'R2 uploads' },
  { key: 'priority_support', label: 'Priority support' },
];

const formatDate = (value) => {
  if (!value) return 'Never';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString(undefined, { dateStyle: 'medium' });
};

const statusClassName = (status) => {
  if (status === 'approved' || status === 'active') return 'bg-emerald-50 text-emerald-700';
  if (status === 'rejected' || status === 'past_due' || status === 'canceled') return 'bg-red-50 text-red-700';
  return 'bg-amber-50 text-amber-700';
};

function AdminPage({ user }) {
  const [users, setUsers] = useState([]);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [savingUserId, setSavingUserId] = useState('');
  const isAdmin = user?.profile?.role === 'admin' || user?.role === 'admin';

  const metrics = useMemo(() => ({
    total: users.length,
    pending: users.filter((item) => item.approvalStatus === 'pending').length,
    approved: users.filter((item) => item.approvalStatus === 'approved').length,
    paid: users.filter((item) => ['pro', 'business', 'enterprise'].includes(item.subscriptionPlan)).length,
  }), [users]);

  useEffect(() => {
    if (!isAdmin) {
      setIsLoading(false);
      return undefined;
    }

    let isMounted = true;
    setError('');
    setIsLoading(true);

    userService.listUsers()
      .then((nextUsers) => {
        if (isMounted) setUsers(nextUsers);
      })
      .catch((nextError) => {
        if (isMounted) setError(nextError.message || 'Unable to load admin users.');
      })
      .finally(() => {
        if (isMounted) setIsLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [isAdmin]);

  const updateLocalUser = (uid, changes) => {
    setUsers((currentUsers) => currentUsers.map((item) => (
      item.uid === uid || item.id === uid ? { ...item, ...changes } : item
    )));
  };

  const saveUserAccess = async (targetUser) => {
    const uid = targetUser.uid || targetUser.id;
    setError('');
    setSavingUserId(uid);

    try {
      const saved = await userService.updateUserAccess(uid, targetUser);
      updateLocalUser(uid, saved);
    } catch (nextError) {
      setError(nextError.message || 'Unable to update user access.');
    } finally {
      setSavingUserId('');
    }
  };

  const toggleFeature = (targetUser, featureKey) => {
    const uid = targetUser.uid || targetUser.id;
    const currentFeatures = Array.isArray(targetUser.featureKeys) ? targetUser.featureKeys : [];
    const nextFeatures = currentFeatures.includes(featureKey)
      ? currentFeatures.filter((key) => key !== featureKey)
      : [...currentFeatures, featureKey];

    updateLocalUser(uid, { featureKeys: nextFeatures });
  };

  if (!user) {
    return (
      <section className="mx-auto max-w-7xl px-5 py-6 sm:px-8">
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          Sign in with an admin account to manage Sharabl users.
        </div>
      </section>
    );
  }

  if (!isAdmin) {
    return (
      <section className="mx-auto max-w-7xl px-5 py-6 sm:px-8">
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          Your account does not have admin access.
        </div>
      </section>
    );
  }

  return (
    <section className="mx-auto max-w-7xl px-5 py-6 sm:px-8">
      <div className="mb-6">
        <p className="label-caps">Admin</p>
        <h1 className="mt-2 text-3xl font-semibold tracking-normal">Users and entitlements</h1>
        <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-600">
          Approve accounts, assign subscriptions, and control feature access for Sharabl users.
        </p>
      </div>

      {error && (
        <div className="mb-5 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="mb-5 grid gap-4 md:grid-cols-4">
        <div className="stat-card">
          <span>Users</span>
          <strong>{metrics.total}</strong>
        </div>
        <div className="stat-card">
          <span>Pending</span>
          <strong>{metrics.pending}</strong>
        </div>
        <div className="stat-card">
          <span>Approved</span>
          <strong>{metrics.approved}</strong>
        </div>
        <div className="stat-card">
          <span>Paid plans</span>
          <strong>{metrics.paid}</strong>
        </div>
      </div>

      <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
        <div className="grid gap-4 border-b border-slate-200 bg-slate-50 px-4 py-3 text-xs font-semibold uppercase text-slate-500 lg:grid-cols-[1.2fr_0.6fr_0.7fr_1fr_1.4fr_auto]">
          <span>User</span>
          <span>Approval</span>
          <span>Role</span>
          <span>Subscription</span>
          <span>Features</span>
          <span>Action</span>
        </div>

        {isLoading ? (
          <div className="px-4 py-6 text-sm text-slate-500">Loading users...</div>
        ) : users.length ? users.map((managedUser) => {
          const uid = managedUser.uid || managedUser.id;
          const featureKeys = Array.isArray(managedUser.featureKeys) ? managedUser.featureKeys : [];

          return (
            <div
              className="grid gap-4 border-b border-slate-100 px-4 py-4 last:border-b-0 lg:grid-cols-[1.2fr_0.6fr_0.7fr_1fr_1.4fr_auto] lg:items-start"
              key={uid}
            >
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-slate-950">
                  {managedUser.displayName || managedUser.email || 'Unnamed user'}
                </p>
                <p className="mt-1 truncate text-xs text-slate-500">{managedUser.email}</p>
                <p className="mt-2 text-xs text-slate-500">Joined {formatDate(managedUser.createdAt)}</p>
              </div>

              <div>
                <select
                  className="field"
                  value={managedUser.approvalStatus || 'pending'}
                  onChange={(event) => updateLocalUser(uid, { approvalStatus: event.target.value })}
                >
                  {approvalOptions.map((option) => <option key={option} value={option}>{option}</option>)}
                </select>
                <span className={`mt-2 inline-block rounded px-2 py-1 text-xs font-semibold uppercase ${statusClassName(managedUser.approvalStatus)}`}>
                  {managedUser.approvalStatus || 'pending'}
                </span>
              </div>

              <select
                className="field"
                value={managedUser.role || 'user'}
                onChange={(event) => updateLocalUser(uid, { role: event.target.value })}
              >
                {roleOptions.map((option) => <option key={option} value={option}>{option}</option>)}
              </select>

              <div className="grid gap-2">
                <select
                  className="field"
                  value={managedUser.subscriptionPlan || 'free'}
                  onChange={(event) => updateLocalUser(uid, { subscriptionPlan: event.target.value })}
                >
                  {subscriptionPlans.map((option) => <option key={option} value={option}>{option}</option>)}
                </select>
                <select
                  className="field"
                  value={managedUser.subscriptionStatus || 'trialing'}
                  onChange={(event) => updateLocalUser(uid, { subscriptionStatus: event.target.value })}
                >
                  {subscriptionStatuses.map((option) => <option key={option} value={option}>{option}</option>)}
                </select>
              </div>

              <div className="grid gap-2 sm:grid-cols-2">
                {availableFeatures.map((feature) => (
                  <label
                    className="flex items-center gap-2 rounded border border-slate-200 bg-slate-50 px-3 py-2 text-xs font-semibold text-slate-700"
                    htmlFor={`${uid}-${feature.key}`}
                    key={feature.key}
                  >
                    <input
                      checked={featureKeys.includes(feature.key)}
                      id={`${uid}-${feature.key}`}
                      onChange={() => toggleFeature(managedUser, feature.key)}
                      type="checkbox"
                    />
                    <span>{feature.label}</span>
                  </label>
                ))}
              </div>

              <button
                className="rounded bg-slate-950 px-4 py-2 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:bg-slate-300"
                disabled={savingUserId === uid}
                onClick={() => saveUserAccess(managedUser)}
                type="button"
              >
                {savingUserId === uid ? 'Saving...' : 'Save'}
              </button>
            </div>
          );
        }) : (
          <div className="px-4 py-6 text-sm text-slate-500">No users found.</div>
        )}
      </div>
    </section>
  );
}

export default AdminPage;
